"use client";

import { useState, useEffect } from "react";
import { ArrowRight, Award, Cloud, Cpu, GraduationCap, Network, Users } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const benefits = [{
  icon: Users,
  titleEn: "R&D Development",
  titleZh: "研發與產品工程",
  descriptionEn: "Comprehensive product engineering from initial concept to final prototype, ensuring design feasibility and market readiness.",
  descriptionZh: "從最初概念到最終原型的全方位產品工程設計，確保設計可行性並具備市場競爭力。"
}, {
  icon: Cpu,
  titleEn: "Factory Production",
  titleZh: "自設廠房生產",
  descriptionEn: "Two dedicated manufacturing facilities specializing in high-quality plastic injection and plush toy production at scale.",
  descriptionZh: "擁有兩座自設專業製造基地，專注於高品質塑膠注塑及毛絨玩具的規模化生產。"
}, {
  icon: Network,
  titleEn: "Global Compliance",
  titleZh: "全球合規檢測",
  descriptionEn: "Rigorous testing protocols through BV, ITS, SGS, and TUV Rheinland to meet international safety and quality standards.",
  descriptionZh: "通過 BV、ITS、SGS 及 TUV Rheinland 等權威機構的嚴格檢測流程，完全符合國際安全及品質標準。"
}, {
  icon: GraduationCap,
  titleEn: "Market Certification",
  titleZh: "市場准入認證",
  descriptionEn: "Expert navigation of regional regulatory requirements, including CCC for China and G-Mark for Gulf States markets.",
  descriptionZh: "專業處理各國及地區法規要求，包括中國 CCC 認證及海灣國家 G-Mark 認證。"
}, {
  icon: Cloud,
  titleEn: "ODM Solutions",
  titleZh: "ODM 定制方案",
  descriptionEn: "Ready-to-market product designs tailored for global brands, streamlining your supply chain and reducing time-to-market.",
  descriptionZh: "為全球品牌量身打造可即時推向市場的成熟產品設計，優化供應鏈並大幅縮短上市週期。"
}, {
  icon: Award,
  titleEn: "Quality Assurance",
  titleZh: "全方位品質保證",
  descriptionEn: "End-to-end quality control processes ensuring every unit meets the highest standards of durability and safety.",
  descriptionZh: "貫穿全程的嚴格品控體系，確保每件出廠產品均達到耐用度與安全性的最高標準。"
}];
function BenefitCard({
  benefit,
  index,
  isZh
}: {
  benefit: typeof benefits[0];
  index: number;
  isZh: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const Icon = benefit.icon;
  return <div ref={ref} data-index={index} className="group relative border border-border bg-background p-8 md:p-10 transition-all duration-200 ease-out hover:border-primary" style={{
    transform: isVisible ? "translateY(0)" : "translateY(24px)",
    opacity: isVisible ? 1 : 0,
    transition: `transform 420ms cubic-bezier(.2,.8,.2,1) ${index * 80}ms, opacity 420ms cubic-bezier(.2,.8,.2,1) ${index * 80}ms`
  }}>
      <Icon className="h-6 w-6 text-primary transition-colors duration-200" strokeWidth={1.75} />
      <h3 className="font-default mt-8 text-xl md:text-2xl font-semibold text-foreground tracking-tight">
        {isZh ? benefit.titleZh : benefit.titleEn}
      </h3>
      <p className="mt-3 text-base text-muted-foreground leading-relaxed">
        {isZh ? benefit.descriptionZh : benefit.descriptionEn}
      </p>
    </div>;
}
export default function Expertise() {
  const [isZh, setIsZh] = useState(false);
  useEffect(() => {
    if (typeof window !== "undefined") {
      setIsZh(localStorage.getItem("elite-language") === "zh-Hant");
    }
  }, []);
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="9713" id="our-core-expertise" className="bg-background text-foreground py-28 md:py-36 px-6 md:px-8">
      <div className="mx-auto max-w-[1440px]">
        <div ref={headerRef} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 md:mb-20" style={{
        transform: headerVisible ? "translateY(0)" : "translateY(24px)",
        opacity: headerVisible ? 1 : 0,
        transition: "transform 420ms cubic-bezier(.2,.8,.2,1), opacity 420ms cubic-bezier(.2,.8,.2,1)"
      }}>
          <div className="lg:col-span-7">
            <p className="font-mono text-xs md:text-sm font-medium uppercase tracking-[0.12em] text-muted-foreground mb-6">
              {isZh ? "// 核心優勢與專業能力" : "// Core Competencies"}
            </p>
            <h2 className="font-default text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold tracking-tight leading-[0.95] text-foreground">
              {isZh ? <>
                  為全球品牌打造{" "}
                  <span className="text-primary">
                    精準製造。
                  </span>
                </> : <>
                  Precision manufacturing for{" "}
                  <span className="text-primary">
                    global brands.
                  </span>
                </>}
            </h2>
          </div>

          <div className="lg:col-span-5 flex flex-col justify-end">
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              {isZh ? "Elite Group Corporation Limited 為玩具及消費品製造提供流暢的一站式解決方案，在創意設計與規模化工業生產之間架起橋樑。" : "Elite Group Corporation Limited provides a seamless, one-stop solution for toy and consumer goods manufacturing, bridging the gap between creative design and industrial-scale production."}
            </p>
            <div className="mt-8">
              <Link to="#" className="inline-flex items-center gap-2 text-base font-medium text-foreground transition-colors hover:text-primary" style={{
              textDecoration: "underline",
              textDecorationColor: "var(--primary)",
              textDecorationThickness: "2px",
              textUnderlineOffset: "4px"
            }}>
                {isZh ? "探索我們的製造實力" : "Explore our manufacturing capabilities"}
                <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
              </Link>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, i) => <BenefitCard key={i} benefit={benefit} index={i} isZh={isZh} />)}
        </div>

        <div className="mt-16 md:mt-20 flex flex-col sm:flex-row items-start sm:items-center gap-6 pt-12 border-t border-border">
          <p className="font-default text-2xl md:text-3xl font-semibold tracking-tight text-foreground max-w-xl">
            {isZh ? "準備好探討您的下一個生產項目了嗎？" : "Ready to discuss your next production project?"}
          </p>
          <div className="sm:ml-auto">
            <Button asChild className="h-auto bg-primary text-primary-foreground border-none px-8 py-5 font-default font-semibold uppercase tracking-wider text-sm hover:bg-primary/90 transition-all duration-150">
              <Link to="#">
                {isZh ? "聯繫我們的團隊" : "Contact Our Team"}
                <ArrowRight className="h-4 w-4 ml-1" strokeWidth={2} />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}